import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/media";
import { TYPE_LABELS } from "@/mocks/media";

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => setIndex((next + items.length) % items.length),
    [items.length],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden rounded-lg bg-primary-950 md:h-[560px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点推荐"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.heroImage}
            alt={`${item.title} 剧照`}
            title={`${item.title} - WWW.17C`}
            className={`h-full w-full object-cover object-top ${i === index ? "animate-ken-burns" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 p-6 md:p-12">
            <div className="max-w-2xl">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-primary-950">
                  {TYPE_LABELS[item.type]}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                  <i className="ri-star-fill text-accent-400"></i>
                  {item.rating.toFixed(1)}
                </span>
                <span className="text-xs text-background-300">
                  {item.year} · {item.region} · {item.genres.slice(0, 2).join(" / ")}
                </span>
              </div>
              <h3 className="font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
                {item.title}
              </h3>
              <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {item.summary}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${item.id}`}
                  className="flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看详情
                </Link>
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-1.5 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-grid-line"></i>
                  更多{TYPE_LABELS[item.type]}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* 左右切换 */}
      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/70 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/70 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* 指示点 */}
      <div className="absolute bottom-4 right-6 z-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}